<template>
  <div>
    <Loader :visible="isLoading" :text="loadingText" />
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-6 pb-0">

      <div class="mb-6 border-b border-gray-200 pb-4">
        <label class="block text-sm font-medium mb-3 text-gray-700">{{ $t("gm.search_method") }}</label>
        <div class="flex flex-wrap items-center gap-6">
            <div class="flex items-center cursor-pointer">
                <input id="mode-storage" type="radio" value="storage" v-model="searchMode" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                <label for="mode-storage" class="ml-2 block text-sm text-gray-900 cursor-pointer">
                    {{ $t("gm.by_storage") }}
                </label>
            </div>
            <div class="flex items-center cursor-pointer">
                <input id="mode-item" type="radio" value="item" v-model="searchMode" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                <label for="mode-item" class="ml-2 block text-sm text-gray-900 cursor-pointer">
                    {{ $t("gm.by_item") }}
                </label>
            </div>
            <div class="flex items-center cursor-pointer">
                <input id="mode-lot" type="radio" value="lot" v-model="searchMode" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                <label for="mode-lot" class="ml-2 block text-sm text-gray-900 cursor-pointer">
                    {{ $t("gm.by_lot") }}
                </label>
            </div>
            <div class="flex items-center cursor-pointer">
                <input id="mode-barcode" type="radio" value="barcode" v-model="searchMode" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                <label for="mode-barcode" class="ml-2 block text-sm text-gray-900 cursor-pointer">
                    {{ $t("gm.by_barcode") }}
                </label>
            </div>
        </div>
      </div>

      <div v-if="searchMode === 'storage'" class="mb-6 border-b border-gray-200 pb-4">
        <label class="block text-sm font-medium mb-6 ">{{ $t('gm.warehouse_choice') }}</label>
        <div class="flex gap-2">
          <select
            v-model="selectedWarehouse"
            class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style="background-color: #fff;"
            @change="onWarehouseChange"
          >
            <option disabled value=""> {{ $t('gm.warehouse_choice') }}  </option>
            <option v-for="storage in storages" :key="storage.Storage" :value="storage.Storage">{{ storage.Storage }} ( {{ storage.Description }} )</option>
          </select>
          <button 
            v-if="selectedWarehouse"
            class="text-white bg-gray-500 hover:bg-gray-600 font-medium rounded-lg text-sm px-4 py-2 text-center"
            @click="resetWarehouse"
          >
            {{ $t('actions.reset') || 'Reset' }}
          </button>
        </div>
      </div>

      <div v-if="isSearchReady">
        
        <div class="flex items-center justify-between mb-4">
            <label class="block text-sm font-medium mb-2">
              {{ searchMode === 'storage' ? $t('core.article_list') : $t('core.search') }}
            </label>
        </div>

        <div class="mb-6">
          <div class="relative">
            <input
              ref="barcodeInput"
              v-model="barcodeSearch"
              type="text"
              :placeholder="placeholderSearch"
              class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              @keyup.enter="performSearch"
              autofocus
            >
            <div class="absolute right-3 top-3 text-gray-400">
              <i class="fa fa-search"></i>
            </div>
          </div>
        </div>

        <!-- Tabella server-side per storage mode -->
        <div v-if="searchMode === 'storage' && selectedWarehouse && filtersApplied">
            <PageServerTable
                :headers="headersFiltered"
                :items="items"
                :items-per-page.sync="ipp"
                :items-per-page-options="ippo"
                :sort-by="sortby"
                :items-length="count"
                :reload-fn="reload"
                :item-value="'Item'"
                :page="page"
                show-current-page
            >
                <template #item.select="{ item }">
                    <div class="flex items-center space-x-2">
                        <button 
                            class="text-white bg-blue-400 dark:bg-blue-500 font-medium rounded-lg text-sm px-3 py-1.5 text-center" 
                            @click="addItems([item], false, true)"
                        >
                            +
                        </button>
                    </div>
                </template>
            </PageServerTable>
        </div>
        
        <br>
        
      </div>

      <div class="mt-8">
        <p class="font-bold text-lg mb-2">{{ $t('modules.rettifica') }} ({{ inventoryrows.length }}):</p>
        <div class="flex gap-2 mb-2">
          <button
            v-if="inventoryrows.length > 0"
            class="text-white bg-red-400 dark:bg-red-500 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            @click="inventoryrows.splice(0, inventoryrows.length)"
          >
            {{ $t('actions.empty') }}
          </button>
          <button
            v-if="inventoryrows.length > 0"
            class="text-white bg-blue-400 dark:bg-blue-500 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSaving"
            @click="salvaRettifica()"
          >
            <span v-if="isSaving">{{ $t('core.saving_data') }}...</span>
            <span v-else>{{ $t('actions.save') }}</span>
          </button>
        </div>

        <PageNormalTable
          :headers="headersInventory"
          :items="inventoryrows"
          :item-value="'Item'"
          hide-default-footer
          hide-delete-confirm
          id="userpicks"
          density="compact"
        >
          <template #item.Item="{ item }">
            <div class="flex flex-col">
                <span class="font-semibold text-gray-900">{{ item.Item }}</span>
                <span class="text-xs text-gray-500">{{ item.Description }}</span>
            </div>
          </template>

          <template #item.Storage="{ item }">
            <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200" v-if="item.Storage">
                {{ item.Storage }}
            </span>
            <span v-else class="text-gray-300 italic">-</span>
          </template>

          <template #item.Lot="{ item }">
              <div v-if="item.Lot">
                  <span v-if="item.Lot" class="font-mono text-sm bg-yellow-50 text-yellow-800 px-2 py-1 border border-yellow-200 rounded">
                    {{ item.Lot }}
                  </span>
              </div>
              <div v-else>
                  <span class="text-gray-300 text-xs italic">-</span>
              </div>
          </template>
          <template #item.Specificator="{ item }">
              <div v-if="item.Specificator">
                  <div class="text-[10px] text-gray-500 mt-1 uppercase">
                    {{ item.SpecificatorType == MagoEnums.Specificator_Type.Customer ? $t('gm.customer_short') : (item.SpecificatorType == MagoEnums.Specificator_Type.Supplier ? $t('gm.supplier_short') : '') }}
                    
                  </div>
                  <div class="text-xs font-semibold text-gray-700 truncate max-w-[200px]" :title="item.SpecificatorName">
                    {{ item.SpecificatorName }}
                  </div>
              </div>
              <div v-else>
                  <span class="text-gray-300 text-xs italic">-</span>
              </div>
          </template>

          <template #item.NewQty="{ item }">
            <span>
              <input 
                type="number" 
                class="w-full p-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right" 
                v-model.number="item.NewQty" 
                onfocus="this.select()"
              >
            </span>
          </template>
        </PageNormalTable>
      </div>
    </div>

    <ConfirmationModal :show="showSelectionModal" @close="showSelectionModal = false">
      <template #title>
        {{ $t('gm.select_item_lot') }}
      </template>
      <template #content>
        <div class="max-h-96 overflow-y-auto">
            <p class="text-sm text-gray-600 mb-4">{{ $t('gm.results_found') }}</p>
            <label class="flex items-center gap-2 mb-2 text-sm">
                <input type="checkbox" v-model="showZeroStock">
                {{ $t('gm.show_zero_stock') }}
            </label>
            <div v-for="(res, index) in filteredSearchResults" :key="index" 
                 class="p-3 mb-2 border rounded hover:bg-blue-50 cursor-pointer flex justify-between items-center group"
                 @click="selectSearchResult(res)">
                <div>
                    <div class="font-bold">{{ res.Item }}</div>
                    <div class="text-sm text-gray-600">{{ res.Description }}</div>
                    <div class="flex flex-wrap gap-2 mt-1">
                        <span v-if="res.Lot" class="text-xs font-mono bg-yellow-50 px-1 border border-yellow-200 rounded">{{ $t('gm.lot_short') }} {{ res.Lot }}</span>
                        <span class="text-xs font-mono bg-gray-50 px-1 border border-gray-200 rounded">{{ $t('gm.storage_short') }} {{ res.Storage }}</span>
                        <div v-if="res.Specificator" class="flex flex-col">
                            
                            <span class="text-[10px] font-semibold text-blue-900 truncate max-w-[150px]">{{ res.SpecificatorName }}</span>
                        </div>
                        
                    </div>
                </div>
                <div class="text-right">
                    <div class="text-sm font-semibold">{{ $t('gm.stock_short') }} {{ Number(res.BookInv) }}</div>
                </div>
            </div>
        </div>
      </template>
      <template #footer>
        <SecondaryButton @click="showSelectionModal = false">
          {{ $t('actions.cancel') }}
        </SecondaryButton>
      </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showBarCodeModal" @close="showBarCodeModal = false">
      <template #title>
        {{ $t('gm.insert_qty') }}
      </template>
      <template #content>
        <div v-if="barcodeItem">
            <p>{{ $t('gm.insert_qty_for') }} <b>{{ barcodeItem.Item }}</b></p>
            <div class="text-sm text-gray-500 mb-2">
                <span v-if="barcodeItem.Storage">Deposito: {{ barcodeItem.Storage }}</span>
                <span v-if="barcodeItem.Lot"> - Lotto: {{ barcodeItem.Lot }}</span>
                <div v-if="barcodeItem.Specificator" class="mt-1">
                    <span class="text-xs">({{ barcodeItem.SpecificatorType == MagoEnums.Specificator_Type.Customer ? 'Cliente' : (barcodeItem.SpecificatorType == MagoEnums.Specificator_Type.Supplier ? 'Fornitore' : '') }})</span>
                    <div class="text-xs italic">{{ barcodeItem.SpecificatorName }}</div>
                </div>
            </div>
            <input
            ref="barcodeQtyInput"
            v-model.number="barcodeQty"
            onfocus="this.select()"
            type="number"
            min="0"
            class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keyup.enter="setBarcodeQty" 
            >
            <div class="mt-2 text-sm text-gray-600">{{ $t('core.available') }}: <b>{{ Number(barcodeItem.BookInv) }}</b></div>
        </div>
      </template>
      <template #footer>
        <SecondaryButton @click="cancelBarcodeQty">
          {{ $t('actions.cancel') }}
        </SecondaryButton>
        <button 
          class="ms-3 inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700 focus:bg-green-700 active:bg-green-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition ease-in-out duration-150"
          @click="azzeraBarcodeQty"
        >
          {{ $t('actions.zero') }}
        </button>
        <PrimaryButton class="ms-3" @click="setBarcodeQty">
          {{ $t('actions.set') }}
        </PrimaryButton>
      </template>
    </ConfirmationModal>

    

    <ToastNotification :show="toast.show" :message="toast.message" :type="toast.type" @update:show="toast.show = $event" />
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import axios from 'axios';
import Loader from '@/Components/Loader.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';

export default {
  props: ['items', 'count', 'ippo', 'headersInventory', 'storages'],
  components: {
    BreadCrumbs, PageServerTable, PageNormalTable,
    ConfirmationModal, PrimaryButton, SecondaryButton, ToastNotification,Loader
  },
  data() {
    return {
      searchMode: 'storage',
      selectedWarehouse: "",
      
      barcodeSearch: '',
      inventoryrows: [],
      
      searchResults: [],
      showSelectionModal: false,

      showBarCodeModal: false,
      barcodeItem: null,
      barcodeQty: 0,
      isNewItem: false,

      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
        { name: this.$t('modules.rettifica'), href: route('modules.gm.rettifica') }
      ],
      showFilters: false,
      headers: [],
      filtersApplied: false,
      sortby: [],
      ipp: 10,
      page: 1,
      isSaving: false,
      toast: { show: false, message: '', type: 'success' },
      showZeroStock: false,
    };
  },
  computed: {
    // Computed per decidere se mostrare l'area di lavoro
    isSearchReady() {
        if (this.searchMode === 'storage') {
            return !!this.selectedWarehouse;
        }
        // Per Item/Lot non serve selezionare nulla prima
        return true; 
    },
    headersFiltered() {
      return this.headers.filter(h => h.value === 'Item' || h.value === 'select');
    },
    placeholderSearch() {
        if (this.searchMode === 'item') return this.$t('gm.insert_item_code');
        if (this.searchMode === 'lot') return this.$t('gm.insert_lot');
        if (this.searchMode === 'barcode') return this.$t('core.barcode_search') + '...';
        return this.$t('core.barcode_search') + '...';
    },
    filteredSearchResults() {
      if (this.showZeroStock) return this.searchResults;
      return this.searchResults.filter(r => Number(r.BookInv) !== 0);
    }
  },
  methods: {
    onWarehouseChange() {
      this.filtersApplied = false;
      this.barcodeSearch = '';
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
      });
    },
    
    resetWarehouse() {
      this.selectedWarehouse = '';
      this.filtersApplied = false;
      this.barcodeSearch = '';
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
      });
    },
    
    performSearch() {
      if (!this.barcodeSearch.trim()) return;
      
      let _this = this;
      let payload = {};

      switch (this.searchMode) {
          case 'storage':
              // Controllo obbligatorio deposito
              if (!this.selectedWarehouse) {
                  this.showToast('Seleziona prima un deposito', 'warning');
                  return;
              }
              payload = { 
                  mode: 'storage',
                  barcode: this.barcodeSearch, // Uso la chiave specifica 'barcode'
                  storage: this.selectedWarehouse
              };
              break;

          case 'item':
              payload = { 
                  mode: 'item',
                  item_code: this.barcodeSearch, // Uso la chiave specifica 'item_code'
              };
              break;

          case 'lot':
              payload = { 
                  mode: 'lot',
                  lot_number: this.barcodeSearch, // Uso la chiave specifica 'lot_number'
              };
              break;

          case 'barcode':
              payload = { 
                  mode: 'barcode',
                  barcode: this.barcodeSearch,
              };
              break;
      }

      axios.post(route('modules.gm.cercabarcode'), payload)
        .then((response) => {
            const results = response.data || [];
            if (results.length === 0) {
                this.showToast('Nessun risultato.', 'warning');
            } 
            else if (results.length === 1) {
                // OTTIMIZZAZIONE: Passo direttamente l'oggetto risultato
                
                this.addItems([results[0]], false, true);
            } 
            else {
                this.searchResults = results;
                this.showSelectionModal = true;
            }
            this.barcodeSearch = ''; 
        })
        .catch(err => {
            console.error(err);
            _this.showToast('Errore durante la ricerca.', 'error');
        });
    },

    selectSearchResult(item) {
        this.showSelectionModal = false;
        
        this.addItems([item], false, true);
    },

    addItems(itemsToAdd, clearselected = false, openQtyModal = true) {
        
        // itemsToAdd deve essere un array di OGGETTI completi (non solo stringhe codici)
        // Es: [{ Item: 'ART01', Description: 'Test', BookInv: 10, Storage: '001', Lot: 'L1' }]

        itemsToAdd.forEach(newItem => {
            // Se manca il deposito e siamo in modalità storage, lo assegniamo
            if (!newItem.Storage && this.searchMode === 'storage' && this.selectedWarehouse) {
                newItem.Storage = this.selectedWarehouse;
            }

            // Normalizzo i numeri per sicurezza
            newItem.BookInv = Number(newItem.BookInv || 0);
            newItem.NewQty = newItem.BookInv; // Default: propongo la giacenza attuale

            // 1. Cerco se esiste già nella lista locale (Carrello)
            // La chiave univoca è Item + Lotto + Deposito + Specificator + SpecificatorType
            let existingIndex = this.inventoryrows.findIndex(row => 
                row.Item === newItem.Item && 
                row.Lot === newItem.Lot && 
                row.Storage === newItem.Storage &&
                row.Specificator === newItem.Specificator &&
                row.SpecificatorType === newItem.SpecificatorType
            );

            if (existingIndex !== -1) {
                // SE ESISTE: Aggiorno i dati (es. la giacenza potrebbe essere cambiata nel DB nel frattempo? 
                // In questo caso usiamo i dati freschi della ricerca)
                // Nota: Manteniamo la NewQty se l'utente l'aveva già modificata? 
                // Per ora sovrascrivo per semplicità, o puoi fare: existing.BookInv = newItem.BookInv;
                this.inventoryrows.splice(existingIndex, 1, newItem);
                this.isNewItem = false;
            } else {
                // SE NON ESISTE: Aggiungo
                this.inventoryrows.push(newItem);
                this.isNewItem = true;
            }
        });

        if (clearselected) {
            this.selected = [];
        }

        if (openQtyModal && itemsToAdd.length > 0) {
            // Apro il modal per l'ultimo articolo aggiunto
            // Nota: itemsToAdd[0] è l'oggetto che abbiamo appena passato
            const itemToEdit = itemsToAdd[0];
            
            // Ritrovo il riferimento nell'array reattivo inventoryrows
            const reactiveRow = this.inventoryrows.find(row => 
                row.Item === itemToEdit.Item && 
                row.Lot === itemToEdit.Lot && 
                row.Storage === itemToEdit.Storage &&
                row.Specificator === itemToEdit.Specificator &&
                row.SpecificatorType === itemToEdit.SpecificatorType
            );

            if (reactiveRow) {
                this.barcodeItem = reactiveRow;
                this.barcodeQty = reactiveRow.BookInv;
                this.showBarCodeModal = true;
            }
        }
    },

    setBarcodeQty() {
      if (this.barcodeItem) {
        this.barcodeItem.NewQty = this.barcodeQty;
      }
      this.showBarCodeModal = false;
      this.$nextTick(() => {
          if(this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
      });
    },

    azzeraBarcodeQty() {
        this.barcodeQty = 0;
        this.setBarcodeQty();
    },
    
    cancelBarcodeQty() {
      if (this.isNewItem && this.barcodeItem) {
        const index = this.inventoryrows.findIndex(row => 
            row.Item === this.barcodeItem.Item && 
            row.Lot === this.barcodeItem.Lot && 
            row.Storage === this.barcodeItem.Storage &&
            row.Specificator === this.barcodeItem.Specificator &&
            row.SpecificatorType === this.barcodeItem.SpecificatorType
        );
        if (index !== -1) {
            this.inventoryrows.splice(index, 1);
        }
      }
      this.showBarCodeModal = false;
      this.$nextTick(() => {
          if(this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
      });
    },

    salvaRettifica() {
      if (this.isSaving) return;
      this.isSaving = true;
      
      this.loadingText =  "Salvataggio in corso"; 
      this.isLoading = true;
      
      axios.post(route('modules.gm.salvaRettifica'), { 
          rows: this.inventoryrows, 
          storage: this.selectedWarehouse,
          searchMode: this.searchMode
      })
      .then((response) => {
        if (response.data.success) {
          this.isLoading = false;
          this.showToast("Rettifica salvata con successo", "success");
          setTimeout(() => window.location.reload(), 1200);
        } else {
            this.isLoading = false;
            let errorMsg = "Errore nel salvataggio";
            const data = response.data.data;
            if (data?.Diagnostic?.Errors?.Error) {
                const err = data.Diagnostic.Errors.Error;
                errorMsg = Array.isArray(err) ? err[0].Message : err.Message;
            } else if (response.data.message) {
                errorMsg = response.data.message;
            }
            this.showToast(errorMsg, "error");
            setTimeout(() => window.location.reload(), 1200);
        }
      })
      .catch(err => {
          this.isSaving = false;
          console.error(err);
          this.showToast("Errore di connessione durante il salvataggio", "error");
          this.isLoading = false;
      });
    },
    reload(sortby, itemsperpage, page) {
        if (sortby) this.sortby = sortby;
        if (itemsperpage) this.ipp = itemsperpage;
        if (page) this.page = page;
        router.post(window.location.href, { 
            headers: this.headers, 
            sortby: this.sortby, 
            itemsperpage: this.ipp, 
            page: this.page, 
            search: this.search,
            storage: this.selectedWarehouse // Passo il deposito selezionato
        }, { only: ['items', 'count'], onSuccess: () => { this.filtersApplied = true; } });
    },
    applyFilters() {
      this.showFilters = false;
      this.reload();
    },
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
  },
  watch: {
    searchMode() {
        this.barcodeSearch = '';
        this.selectedWarehouse = '';
        this.filtersApplied = false;
        this.searchResults = [];
        this.showSelectionModal = false;
        this.$nextTick(() => { 
          if(this.$refs.barcodeInput) this.$refs.barcodeInput.focus(); 
        });
    }
  },
  mounted() {
    this.headers = this.$attrs.headers;
    this.ipp = this.$attrs.ipp;
    this.page = this.$attrs.page;
    this.sortby = this.$attrs.sortby;
  }
};
</script>
